import { Button, Flex, Typography, Progress, Tooltip } from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { IPendingUploadItem } from "../../../types/interfaces";
import { cancelUpload } from "../../../redux/Slices/MediaSlice";
import { useAppDispatch } from "../../../redux/store";
import { UPLOAD_TONE_FILL, uploadFileIcon, uploadRowTone } from "../uploadTone";

/** One upload in the panel: state tile, name, size and status in words, percent, meter. */
const UploadCard = ({ uploadItem }: { uploadItem: IPendingUploadItem }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("media");
  const status = uploadItem.status ?? "pending";
  const tone = uploadRowTone(uploadItem.status);
  const FileIcon = uploadFileIcon(uploadItem.body.name);
  const statusLabel = uploadItem.releasePending
    ? t("upload.rowStatus.releasePending")
    : t(`upload.rowStatus.${status}`);
  const canCancel =
    uploadItem.status === "pending" ||
    uploadItem.status === "uploading" ||
    uploadItem.status === "failed" ||
    uploadItem.releasePending;

  return (
    <div className={`st-upload-row st-upload-row--${tone}`} data-status={status}>
      <Flex justify="space-between" align="center" gap={12}>
        <Flex align="center" gap={12} className="st-upload-row__main">
          <span className={`st-upload-row__tile st-upload-row__tile--${tone}`} aria-hidden="true">
            {uploadItem.status === "completed" ? (
              <CheckOutlined />
            ) : uploadItem.status === "uploading" ? (
              <LoadingOutlined spin />
            ) : (
              <FileIcon />
            )}
          </span>
          <div className="st-upload-row__text">
            <Typography.Text ellipsis strong className="st-upload-row__name">
              {uploadItem.body.name}
            </Typography.Text>
            <Flex align="center" gap="small" className="st-upload-row__meta">
              <Typography.Text type="secondary" className="st-tabular">
                {Math.round(uploadItem.body.size / 1024)} KB
              </Typography.Text>
              <span className="st-upload-row__dot" aria-hidden="true" />
              <Typography.Text type={tone === "failed" ? "danger" : "secondary"}>
                {statusLabel}
              </Typography.Text>
            </Flex>
          </div>
        </Flex>

        <Flex align="center" gap={4}>
          <Typography.Text
            strong
            type={tone === "failed" ? "danger" : undefined}
            className="st-tabular st-upload-row__percent"
          >
            {uploadItem.progress}%
          </Typography.Text>
          {canCancel ? (
            <Tooltip title={t("upload.cancel")}>
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                aria-label={t("upload.cancel")}
                onClick={() => dispatch(cancelUpload(uploadItem.id))}
              />
            </Tooltip>
          ) : null}
        </Flex>
      </Flex>

      {uploadItem.status !== "completed" && uploadItem.status !== "cancelled" && (
        <Progress
          className="st-meter st-upload-row__meter"
          size={[-1, 6]}
          percent={uploadItem.progress}
          showInfo={false}
          strokeColor={UPLOAD_TONE_FILL[tone]}
        />
      )}
    </div>
  );
};

export default UploadCard;
